import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoAwesomeRounded, SchoolRounded, CoPresentRounded, ArrowForwardRounded } from '@mui/icons-material';

/**
 * Public entry page. Lets a visitor pick how they want to continue —
 * student (login + chat) or teacher (gated dashboards) — instead of having
 * to know and type the right URL.
 */
const Landing: React.FC = () => {
  const navigate = useNavigate();

  // Cards lift a hair more on hover than the original so they feel clickable
  // without being noisy. `transition-all duration-200` keeps the hover feel snappy.
  const cardClass =
    'group flex flex-col items-start gap-3 rounded-2xl border border-violet-200 bg-white p-6 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:scale-[1.01] hover:border-violet-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-violet-300';

  return (
    <div className="main-gradient-bg min-h-screen flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      {/* Card fades + slides up on mount (single one-shot animation). */}
      <div className="w-full max-w-2xl space-y-8 rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm sm:p-10 animate-landing-fade-in-up">
        <div className="text-center">
          {/* Title sparkle does a slow, quiet pulse — the only ongoing motion. */}
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 animate-landing-pulse-soft">
            <AutoAwesomeRounded />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">Curiosity Coach</h1>
          <p className="mt-2 text-sm text-violet-700">How would you like to continue?</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button type="button" onClick={() => navigate('/login')} className={cardClass}>
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
              <SchoolRounded />
            </span>
            <span className="text-lg font-semibold text-gray-900">I&apos;m a Student</span>
            <span className="text-sm text-slate-500">Log in and chat with your Curiosity Coach.</span>
            <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-violet-700">
              Continue <ArrowForwardRounded fontSize="small" className="transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>

          <button type="button" onClick={() => navigate('/teacher-login')} className={cardClass}>
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <CoPresentRounded />
            </span>
            <span className="text-lg font-semibold text-gray-900">I&apos;m a Teacher</span>
            <span className="text-sm text-slate-500">View class dashboards, summaries and student analysis.</span>
            <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-indigo-700">
              Continue <ArrowForwardRounded fontSize="small" className="transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Landing;
